import { useState, useLayoutEffect, useEffect, useMemo } from 'react'

import { EventsEventIdDeleteRequest, EventsGetRequest, SmepEventPaginatedList } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery, useInfiniteQuery, useMutationState, InfiniteData, useQueryClient } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'
import { pickBy } from 'lodash-es'

import { eventMutationKeys } from 'hooks/mutations/smep'
import { eventsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { workspaceQueryKeys, eventQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { mergeBatchIntoSource } from 'utils'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ virtualItems, setLoadingRows }: UseAsyncContentCaching) => {
  const queryClient = useQueryClient()

  const { workspaceId } = eventsOverviewRoute.useParams()
  const search = eventsOverviewRoute.useSearch()
  const { query, timeRangeStart, timeRangeEnd } = search

  const sortProperties = useMemo(
    () => pickBy(search, (_, key) => key.startsWith('orderBy')) as PickStartsWith<EventsGetRequest, 'orderBy'>,
    [search],
  )

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const eventsQuery = useInfiniteQuery({
    ...eventQueryKeys.list({
      workspaceId,
      query,
      timeRangeStart,
      timeRangeEnd,
      view: 'Full',
      ...sortProperties,
    }),
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [batchGetEnabled, setBatchGetEnabled] = useState(eventsQuery.isSuccess)

  const pendingEvents = useMutationState({
    filters: { mutationKey: eventMutationKeys.delete(), status: 'pending' },
    select: (mutation) => mutation.state.variables as EventsEventIdDeleteRequest,
  })

  const eventBatchQuery = useQuery({
    ...eventQueryKeys.batchGet(
      {},
      () => virtualItems?.map((virtualItem) => eventsQuery.data?.flat[virtualItem.index]?.id) as string[],
    ),
    enabled: batchGetEnabled,
    refetchInterval: RefetchIntervals.FAST,
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useLayoutEffect(() => {
    if (eventsQuery.isSuccess && eventBatchQuery.isSuccess) {
      queryClient.setQueriesData<InfiniteData<SmepEventPaginatedList>>({ queryKey: eventQueryKeys.lists() }, (old) => {
        if (old) {
          return {
            ...old,
            pages:
              old.pages.map((page) => ({
                ...page,
                items: mergeBatchIntoSource(
                  page.items,
                  eventBatchQuery.data.items,
                  new Set(eventBatchQuery.data.notFoundIds),
                ),
              })) ?? [],
          }
        }
        return old
      })
    }
  }, [
    eventBatchQuery.data?.items,
    eventBatchQuery.data?.notFoundIds,
    eventBatchQuery.isSuccess,
    eventsQuery.isSuccess,
    queryClient,
  ])

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingEvents.map((task) => task.eventId)])])
  }, [pendingEvents, setLoadingRows])

  useEffect(() => {
    if (eventsQuery.isSuccess) {
      if (!batchGetEnabled) {
        const timeoutHandle = setTimeout(() => {
          setBatchGetEnabled(true)
          clearTimeout(timeoutHandle)
        }, 5000)
      }
    }
  }, [batchGetEnabled, eventsQuery.isSuccess])

  return {
    workspaceQuery,
    eventsQuery,
    setBatchGetEnabled,
  }
}
